import logger from "@/utils/logger";
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
// src/services/api/internal-login/internal-user-api-service.ts
import { BaseService } from "@/services/BaseService";
import { buildAuthDeviceInfo, type AuthDeviceInfo } from "@/services/deviceId";

/**
 * Phone number entry for the API
 */
interface PhoneNumberEntry {
    type: "mobile" | "business" | "home";
    number: string;
}

interface MsalLoginData {
    id: string;
    email: string;
    givenName: string;
    surname: string;
    displayName: string;
    jobTitle?: string;
    officeLocation?: string;
    // 🔥 Phone fields from Microsoft Graph API (MSAL users only)
    mobilePhone?: string;
    businessPhones?: string[];
    // 🔥 NEW: Formatted phone numbers array for the API
    phoneNumbers: PhoneNumberEntry[];
    deviceInfo: AuthDeviceInfo;
}

// 🔥 UPDATED UserProfile interface with multiple roles support and phone numbers
interface UserProfile {
    userId: number;
    displayName: string;
    email: string;
    jobTitle?: string;
    officeLocation?: string;
    organization?: string;
    role?: string; // Keep for backward compatibility
    roles?: string[]; // 🔥 NEW: Multiple roles
    isNewUser: boolean;
    organizationId?: number;
    department?: string;
    departmentId?: number;
    // 🔥 UPDATED: Separate mobile and business phone support
    // These fields are populated from Microsoft Graph API for MSAL users
    // External users will have manual phone input functionality added later
    mobilePhone?: string;
    businessPhones?: string[];
    primaryPhoneNumber?: string; // Keep for backward compatibility
    phoneType?: string; // Keep for backward compatibility
    // Profile photo support
    profilePhoto?: string; // Base64 encoded image from DB
    accessToken?: string;
    accessTokenExpiresAt?: string; // ISO date string from API
    refreshToken?: string | null;
    refreshTokenExpiresAt?: string | null;
}

// 🔥 UPDATED: ExternalUserProfile interface with multiple roles support and phone numbers
interface ExternalUserProfile {
    userId: number;
    displayName: string;
    email: string;
    jobTitle?: string;
    officeLocation?: string;
    organization?: string;
    role?: string; // Keep for backward compatibility
    roles?: string[]; // 🔥 NEW: Multiple roles
    isNewUser?: boolean;
    organizationId?: number;
    department?: string;
    departmentId?: number;
    // 🔥 UPDATED: Separate mobile and business phone support
    // For external users: manual phone input functionality will be added later
    // For now, these fields may be empty for external users
    mobilePhone?: string;
    businessPhones?: string[];
    primaryPhoneNumber?: string; // Keep for backward compatibility
    phoneType?: string; // Keep for backward compatibility
    // Profile photo support
    profilePhoto?: string; // Base64 encoded image from DB
    isTemporaryPassword?: boolean;
    accessToken?: string;
    accessTokenExpiresAt?: string; // ISO date string from API
    refreshToken?: string | null;
    refreshTokenExpiresAt?: string | null;
}

// Department interface - matches your existing Organization API response
interface Department {
    departmentId: number;
    departmentName: string;
    departmentDesc?: string;
    isActive: boolean;
}

type DepartmentResponseShape =
    | Department[]
    | string
    | {
          data?: unknown;
          value?: unknown;
          items?: unknown;
          departments?: unknown;
          result?: unknown;
          $values?: unknown;
      };

// Update department request interface
interface UpdateDepartmentData {
    departmentId: number;
}

// 🔥 FIX: Global cache for user profile
let globalCurrentUserProfile: UserProfile | null = null;

// 🔥 FIX: Pending profile request to deduplicate concurrent calls
const pendingProfileRequest: Map<string, Promise<UserProfile>> = new Map();

// 🔥 FIX: Global cache for profile photo checks
const profilePhotoExistsCache: Map<
    number,
    { exists: boolean; timestamp: number }
> = new Map();
const profilePhotoCache: Map<
    number,
    { photo: string | null; timestamp: number }
> = new Map();
const PHOTO_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// 🔥 FIX: Deduplication for msalLogin calls
let pendingMsalLoginRequest: Promise<UserProfile> | null = null;
let lastMsalLoginEmail: string | null = null;

export class UserApiService extends BaseService {
    protected get baseUrl(): string {
        return (
            process.env.NEXT_PUBLIC_API_URL ||
            process.env.NEXT_PUBLIC_API_BASE_URL ||
            "https://localhost:7186"
        );
    }

    constructor() {
        super();
        logger.info("UserApiService initialized with baseUrl:", this.baseUrl);
    }

    // 🔥 FIX: Use global cache for user profile
    private get currentUserProfile(): UserProfile | null {
        return globalCurrentUserProfile;
    }

    private set currentUserProfile(value: UserProfile | null) {
        globalCurrentUserProfile = value;
    }

    private normalizeDepartmentRecord(raw: unknown): Department | null {
        if (!raw || typeof raw !== "object") {
            return null;
        }

        const candidate = raw as Record<string, unknown>;
        const departmentId = Number(candidate.departmentId);
        const departmentName =
            typeof candidate.departmentName === "string"
                ? candidate.departmentName.trim()
                : "";

        if (!Number.isFinite(departmentId) || !departmentName) {
            return null;
        }

        return {
            departmentId,
            departmentName,
            departmentDesc:
                typeof candidate.departmentDesc === "string"
                    ? candidate.departmentDesc
                    : undefined,
            isActive: candidate.isActive !== false,
        };
    }

    private extractDepartmentList(
        raw: DepartmentResponseShape
    ): Department[] | null {
        if (Array.isArray(raw)) {
            return raw
                .map((department) => this.normalizeDepartmentRecord(department))
                .filter((department): department is Department =>
                    Boolean(department)
                );
        }

        if (typeof raw === "string") {
            const trimmed = raw.trim();
            if (!trimmed) {
                return [];
            }

            if (!trimmed.startsWith("[") && !trimmed.startsWith("{")) {
                return null;
            }

            try {
                return this.extractDepartmentList(
                    JSON.parse(trimmed) as DepartmentResponseShape
                );
            } catch {
                return null;
            }
        }

        if (raw && typeof raw === "object") {
            const candidate = raw as Record<string, unknown>;
            const keys = [
                "data",
                "value",
                "items",
                "departments",
                "result",
                "$values",
            ] as const;

            for (const key of keys) {
                if (!(key in candidate)) {
                    continue;
                }

                const extracted = this.extractDepartmentList(
                    candidate[key] as DepartmentResponseShape
                );
                if (extracted !== null) {
                    return extracted;
                }
            }
        }

        return null;
    }

    // Enhanced API call wrapper for validation error handling
    private async apiCallWithEnhancedErrors<T, D = unknown>(
        method: string,
        path: string,
        data?: D
    ): Promise<T> {
        try {
            return await this.request<T, D>(method, path, data);
        } catch (error: any) {
            // Check if this is an HTTP error that might contain validation details
            if (error.message && error.message.startsWith("HTTP")) {
                // Extract response text from the error message
                const responseText = error.message
                    .substring(error.message.indexOf(":") + 1)
                    .trim();

                try {
                    const errorData = JSON.parse(responseText);
                    if (errorData.errors) {
                        console.error("Validation errors:", errorData.errors);
                        const errorMessages = Object.entries(errorData.errors)
                            .map(
                                ([field, messages]: [string, any]) =>
                                    `${field}: ${Array.isArray(messages) ? messages.join(", ") : messages}`
                            )
                            .join("; ");
                        throw new Error(`Validation errors: ${errorMessages}`);
                    }
                } catch (parseError) {
                    // If not JSON, throw original error
                }
            }

            console.error(
                `Enhanced API call failed for ${method} ${path}:`,
                error
            );
            throw error;
        }
    }

    /**
     * Formats phone numbers from Microsoft Graph API into the API format
     * Filters out empty/null phone numbers
     *
     * @param mobilePhone - Single mobile phone number from Graph API
     * @param businessPhones - Array of business phone numbers from Graph API
     * @param homePhones - Array of home phone numbers from Graph API
     * @returns Array of formatted phone number entries
     */
    private formatPhoneNumbers(
        mobilePhone?: string | null,
        businessPhones?: string[] | null,
        homePhones?: string[] | null
    ): PhoneNumberEntry[] {
        const phoneNumbers: PhoneNumberEntry[] = [];

        // Add mobile phone if present and not empty
        if (mobilePhone && mobilePhone.trim()) {
            phoneNumbers.push({
                type: "mobile",
                number: mobilePhone.trim(),
            });
        }

        // Add business phones if present (filter out empty values)
        if (businessPhones && Array.isArray(businessPhones)) {
            businessPhones
                .filter(
                    (phone): phone is string => !!phone && phone.trim() !== ""
                )
                .forEach((phone) => {
                    phoneNumbers.push({
                        type: "business",
                        number: phone.trim(),
                    });
                });
        }

        // Add home phones if present (filter out empty values)
        if (homePhones && Array.isArray(homePhones)) {
            homePhones
                .filter(
                    (phone): phone is string => !!phone && phone.trim() !== ""
                )
                .forEach((phone) => {
                    phoneNumbers.push({
                        type: "home",
                        number: phone.trim(),
                    });
                });
        }

        return phoneNumbers;
    }

    // MSAL login - saves/updates user data during login
    async msalLogin(
        graphProfile: any,
        aadAccessToken: string
    ): Promise<UserProfile> {
        const email = graphProfile.mail || graphProfile.userPrincipalName || "";

        // Deduplicate concurrent msalLogin calls for the same user.
        if (pendingMsalLoginRequest && lastMsalLoginEmail === email) {
            logger.info("🔄 Reusing pending msalLogin request for:", email);
            return pendingMsalLoginRequest;
        }

        try {
            logger.info("Input Graph Profile:", graphProfile);

            const deviceInfo = buildAuthDeviceInfo();

            // Build display name with fallbacks
            const displayName =
                graphProfile.displayName ||
                (graphProfile.givenName && graphProfile.surname
                    ? `${graphProfile.givenName} ${graphProfile.surname}`
                    : "") ||
                graphProfile.mail ||
                graphProfile.userPrincipalName ||
                "Unknown User";

            const phoneNumbers = this.formatPhoneNumbers(
                graphProfile.mobilePhone,
                graphProfile.businessPhones,
                graphProfile.homePhones
            );

            const msalLoginData: MsalLoginData = {
                id: graphProfile.id || "",
                email:
                    graphProfile.mail || graphProfile.userPrincipalName || "",
                givenName: graphProfile.givenName || "",
                surname: graphProfile.surname || "",
                displayName: displayName.trim(),
                jobTitle: graphProfile.jobTitle || "",
                officeLocation: graphProfile.officeLocation || "",
                mobilePhone: graphProfile.mobilePhone || "",
                businessPhones: graphProfile.businessPhones || [],
                phoneNumbers,
                deviceInfo,
            };

            logger.info("📱 Phone data from Microsoft Graph API:", {
                mobilePhone: graphProfile.mobilePhone,
                businessPhones: graphProfile.businessPhones,
                homePhones: graphProfile.homePhones,
                hasMobilePhone: !!graphProfile.mobilePhone,
                businessPhoneCount: graphProfile.businessPhones?.length || 0,
                homePhoneCount: graphProfile.homePhones?.length || 0,
                formattedPhoneNumbers: phoneNumbers.length,
            });

            const requiredFields = ["id", "email", "displayName"];
            const missingFields = requiredFields.filter(
                (field) => !msalLoginData[field as keyof MsalLoginData]
            );

            if (missingFields.length > 0) {
                throw new Error(
                    `Missing required fields: ${missingFields.join(", ")}`
                );
            }

            logger.info(
                "Calling MSAL login AUTHZ API with validated data:",
                msalLoginData.email
            );

            lastMsalLoginEmail = email;
            pendingMsalLoginRequest = (async () => {
                try {
                    const response = await fetch(
                        `${this.baseUrl.replace(/\/$/, "")}/api/user/msal-login-authz`,
                        {
                            method: "POST",
                            credentials: "include",
                            headers: {
                                "Content-Type": "application/json",
                                // Send AAD token via custom header so Dispatch JWT middleware ignores it.
                                "X-AAD-Authorization": `Bearer ${aadAccessToken}`,
                            },
                            // Backend uses claims; send only minimal context payload.
                            body: JSON.stringify({
                                email: msalLoginData.email,
                                displayName: msalLoginData.displayName,
                                deviceInfo: msalLoginData.deviceInfo,
                            }),
                        }
                    );

                    if (!response.ok) {
                        const text = await response.text();
                        throw new Error(
                            `MSAL login failed ${response.status}: ${text || response.statusText}`
                        );
                    }

                    const apiUserProfile: UserProfile = await response.json();

                    logger.info("🔥 MSAL login-authz response with roles:", {
                        userId: apiUserProfile.userId,
                        roles: apiUserProfile.roles,
                        roleCount: apiUserProfile.roles?.length || 0,
                        singleRole: apiUserProfile.role,
                    });

                    globalCurrentUserProfile = apiUserProfile;
                    this.currentUserProfile = apiUserProfile;

                    return apiUserProfile;
                } finally {
                    pendingMsalLoginRequest = null;
                    lastMsalLoginEmail = null;
                }
            })();

            return pendingMsalLoginRequest;
        } catch (error) {
            console.error("MSAL login AUTHZ API call failed:", error);
            throw error;
        }
    }

    // 🔥 FIX: Clear the cached user profile (for refresh scenarios)
    clearUserProfileCache(): void {
        globalCurrentUserProfile = null;
        this.currentUserProfile = null;
        logger.info("🔄 User profile cache cleared (both global and instance)");
    }

    // Get user profile from your API - used for existing sessions
    async getUserProfile(
        email: string,
        forceRefresh: boolean = false
    ): Promise<UserProfile> {
        const emailLower = email.toLowerCase();

        // 🔥 DEBUG: Log caller to identify duplicate call sources
        logger.info(
            "📞 getUserProfile called for:",
            email,
            "| forceRefresh:",
            forceRefresh,
            "| caller:",
            new Error().stack?.split("\n")[2]?.trim()
        );

        // 🔥 FIX: Check if we have a GLOBAL cached profile for this email (unless force refresh)
        if (
            !forceRefresh &&
            globalCurrentUserProfile &&
            globalCurrentUserProfile.email?.toLowerCase() === emailLower
        ) {
            logger.info("✅ Using globally cached user profile for:", email);
            return globalCurrentUserProfile;
        }

        // 🔥 FIX: Check if there's already a pending request for this email
        const pendingRequest = pendingProfileRequest.get(emailLower);
        if (pendingRequest && !forceRefresh) {
            logger.info("🔄 Reusing pending profile request for:", email);
            return pendingRequest;
        }

        logger.info(
            "🌐 ACTUAL API CALL - Fetching user profile from API for email:",
            email
        );

        if (!email) {
            throw new Error("Email is required to fetch user profile");
        }

        // 🔥 FIX: Create and store the pending request
        const request = (async () => {
            try {
                const path = `/api/user/profile?email=${encodeURIComponent(email)}`;
                const userProfile = await this.request<UserProfile>(
                    "GET",
                    path
                );

                // 🔥 FIX: Store in GLOBAL cache instead of instance cache
                globalCurrentUserProfile = userProfile;
                this.currentUserProfile = userProfile; // Also update instance for backward compat

                // 🔥 ADD DEBUG LOG FOR ROLES
                logger.info("🔥 User profile fetched with roles:", {
                    userId: userProfile.userId,
                    roles: userProfile.roles,
                    roleCount: userProfile.roles?.length || 0,
                    singleRole: userProfile.role, // backward compatibility
                });

                return userProfile;
            } finally {
                pendingProfileRequest.delete(emailLower);
            }
        })();

        pendingProfileRequest.set(emailLower, request);
        return request;
    }

    // 🔥 NEW: Get external user profile from your API
    async getExternalUserProfile(email: string): Promise<ExternalUserProfile> {
        const emailLower = email.toLowerCase();

        // 🔥 DEBUG: Log caller to identify duplicate call sources
        logger.info(
            "📞 getExternalUserProfile called for:",
            email,
            "| caller:",
            new Error().stack?.split("\n")[2]?.trim()
        );

        if (!email) {
            throw new Error("Email is required to fetch external user profile");
        }

        // 🔥 FIX: Check GLOBAL cached profile first (internal profile can be used)
        const cachedProfile =
            globalCurrentUserProfile || this.currentUserProfile;
        if (
            cachedProfile &&
            cachedProfile.email?.toLowerCase() === emailLower
        ) {
            logger.info(
                "✅ Using globally cached user profile for external user:",
                email
            );
            return this.convertToExternalProfile(cachedProfile);
        }

        // 🔥 FIX: Check if there's already a pending request for this email (reuse getUserProfile dedup)
        const pendingRequest = pendingProfileRequest.get(emailLower);
        if (pendingRequest) {
            logger.info(
                "🔄 Reusing pending profile request for external user:",
                email
            );
            const profile = await pendingRequest;
            return this.convertToExternalProfile(profile);
        }

        // 🔥 FIX: Create and store the pending request (shared with getUserProfile)
        const request = (async () => {
            try {
                const path = `/api/user/profile?email=${encodeURIComponent(email)}`;
                const userProfile = await this.request<UserProfile>(
                    "GET",
                    path
                );

                // 🔥 FIX: Cache the profile in GLOBAL cache
                globalCurrentUserProfile = userProfile;
                this.currentUserProfile = userProfile;

                return userProfile;
            } finally {
                pendingProfileRequest.delete(emailLower);
            }
        })();

        pendingProfileRequest.set(emailLower, request);
        const userProfile = await request;

        return this.convertToExternalProfile(userProfile);
    }

    // Helper to convert UserProfile to ExternalUserProfile
    private convertToExternalProfile(
        userProfile: UserProfile
    ): ExternalUserProfile {
        return {
            userId: userProfile.userId,
            displayName: userProfile.displayName,
            email: userProfile.email,
            jobTitle: userProfile.jobTitle,
            officeLocation: userProfile.officeLocation,
            organization: userProfile.organization,
            role: userProfile.role,
            roles: userProfile.roles,
            isNewUser: userProfile.isNewUser,
            organizationId: userProfile.organizationId,
            department: userProfile.department,
            departmentId: userProfile.departmentId,
            mobilePhone: userProfile.mobilePhone,
            businessPhones: userProfile.businessPhones,
            primaryPhoneNumber: userProfile.primaryPhoneNumber,
            phoneType: userProfile.phoneType,
            isTemporaryPassword: false,
        };
    }

    // Get departments for an organization - Uses your existing Organization API
    async getDepartmentsByOrganization(orgId: number): Promise<Department[]> {
        try {
            logger.info("Fetching departments for organization:", orgId);

            if (!orgId) {
                throw new Error(
                    "Organization ID is required to fetch departments"
                );
            }

            // Use the organization-scoped endpoint so the client only loads
            // the departments it can actually select for this user.
            const path = `/api/organization/GetDepartmentListForOrganization/${orgId}`;
            const response =
                await this.apiCallWithEnhancedErrors<DepartmentResponseShape>(
                    "GET",
                    path
                );

            const departments = this.extractDepartmentList(response);
            if (departments !== null) {
                return departments;
            }

            throw new Error("Unexpected department response format");
        } catch (error) {
            console.error("Get departments API call failed:", error);
            throw error;
        }
    }

    // Update user department assignment
    async updateUserDepartment(
        departmentId: number,
        userEmail: string
    ): Promise<void> {
        try {
            logger.info("Updating user department:", {
                departmentId,
                userEmail,
            });

            if (!departmentId) {
                throw new Error("Department ID is required");
            }

            if (!userEmail) {
                throw new Error("User email is required");
            }

            const updateData = {
                departmentId,
                email: userEmail, // This matches your backend UpdateDepartmentModel
            };

            logger.info("Sending department update request:", updateData);

            // Use your existing request method
            await this.apiCallWithEnhancedErrors<void>(
                "PUT",
                "/api/user/update-department",
                updateData
            );

            logger.info("Department updated successfully");
        } catch (error) {
            console.error("Update department API call failed:", error);
            throw error;
        }
    }

    // Check if user has a profile photo in the database
    async checkProfilePhotoExists(userId: number): Promise<boolean> {
        try {
            // 🔥 FIX: Check cache first
            const cached = profilePhotoExistsCache.get(userId);
            if (
                cached &&
                Date.now() - cached.timestamp < PHOTO_CACHE_DURATION
            ) {
                logger.info(
                    "✅ Using cached profile photo exists check for user:",
                    userId
                );
                return cached.exists;
            }

            const response = await this.request<{
                userId: number;
                hasProfilePhoto: boolean;
            }>("GET", `/api/profilephoto/${userId}/exists`);

            // 🔥 FIX: Cache the result
            profilePhotoExistsCache.set(userId, {
                exists: response.hasProfilePhoto,
                timestamp: Date.now(),
            });

            return response.hasProfilePhoto;
        } catch (error) {
            console.error("Check profile photo exists API call failed:", error);
            return false; // Default to false if API call fails
        }
    }

    // Update user's profile photo in the database
    async updateProfilePhoto(
        userId: number,
        profilePhotoBase64: string
    ): Promise<void> {
        try {
            await this.request("PUT", `/api/profilephoto/${userId}`, {
                profilePhotoBase64,
            });
            logger.info("Profile photo updated successfully for user:", userId);

            // 🔥 FIX: Invalidate cache after update
            profilePhotoExistsCache.delete(userId);
            profilePhotoCache.delete(userId);
        } catch (error) {
            console.error("Update profile photo API call failed:", error);
            throw error;
        }
    }

    // Get user's profile photo from the database
    async getProfilePhoto(userId: number): Promise<string | null> {
        try {
            // 🔥 FIX: Check cache first
            const cached = profilePhotoCache.get(userId);
            if (
                cached &&
                Date.now() - cached.timestamp < PHOTO_CACHE_DURATION
            ) {
                logger.info("✅ Using cached profile photo for user:", userId);
                return cached.photo;
            }

            const response = await this.request<{
                userId: number;
                profilePhotoBase64: string;
                hasProfilePhoto: boolean;
                lastUpdated: string;
            }>("GET", `/api/profilephoto/${userId}`);

            const photo = response.hasProfilePhoto
                ? response.profilePhotoBase64
                : null;

            // 🔥 FIX: Cache the result
            profilePhotoCache.set(userId, {
                photo,
                timestamp: Date.now(),
            });

            // Also update exists cache
            profilePhotoExistsCache.set(userId, {
                exists: response.hasProfilePhoto,
                timestamp: Date.now(),
            });

            return photo;
        } catch (error) {
            console.error("Get profile photo API call failed:", error);
            return null; // Return null if photo doesn't exist or API fails
        }
    }

    // Test API connectivity - useful for debugging
    async testConnection(): Promise<boolean> {
        try {
            logger.info("Testing API connection...");

            const response = await fetch(`${this.baseUrl}/health`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
            });

            logger.info("API connection test result:", response.status);
            return response.ok;
        } catch (error) {
            console.error("API connection test failed:", error);
            return false;
        }
    }
}

// Export interfaces for use in components
export type {
    UserProfile,
    ExternalUserProfile,
    Department,
    UpdateDepartmentData,
};
